import React, { createContext, useContext, useMemo, useReducer } from 'react';
import { unstable_useBlocker as useBlocker } from 'react-router-dom';

const initialState = {
  isDirty: false,
  hasValidationError: false,
  blocked: false,
  nextAction: null,
};

export const SidebarEditContext = createContext({ ...initialState });

const sidebarEditStateReducer = (state, action) => {
  switch (action.type) {
    case 'reset':
      return initialState;
    case 'block':
      return { ...state, blocked: true, ...action.payload };
    case 'unblock':
      return { ...state, blocked: false };
    default:
      return { ...state, ...action.payload };
  }
};

export const SidebarEditContextProvider = ({ children }) => {
  const [sidebarEditState, dispatchSidebarEditState] = useReducer(
    sidebarEditStateReducer,
    initialState,
  );

  const { ...currentState } = sidebarEditState;
  const blocker = useBlocker(sidebarEditState.isDirty);

  const value = useMemo(
    () => ({
      ...currentState, // isDirty, hasValidationError
      blockIfDirty: (nextAction) => {
        if (currentState.isDirty) {
          // If it's dirty, block the operation and save the next action
          // to be executed after the user confirms the operation
          dispatchSidebarEditState({ type: 'block', payload: { nextAction } });
          return;
        }
        nextAction();
      },
      isValidationErrorModalOpen:
        (blocker.state === 'blocked' || currentState.blocked) &&
        currentState.hasValidationError,
      isConfirmEditModalOpen:
        (blocker.state === 'blocked' || currentState.blocked) &&
        !currentState.hasValidationError,
      setIsDirty: (isDirty) =>
        dispatchSidebarEditState({
          payload: { isDirty },
        }),
      setHasValidationError: (hasValidationError) =>
        dispatchSidebarEditState({
          payload: { hasValidationError },
        }),
      resetEditState: () => dispatchSidebarEditState({ type: 'reset' }),
      closeModalAndProceed: () => {
        if (currentState.nextAction) {
          currentState.nextAction();
        }
        dispatchSidebarEditState({ type: 'reset' });
        if (blocker.state === 'blocked') blocker.proceed();
      },
      closeModalAndCancel: () => {
        dispatchSidebarEditState({ type: 'unblock' });
        if (blocker.state === 'blocked') blocker.reset();
      },
    }),
    [blocker, currentState],
  );

  return (
    <SidebarEditContext.Provider value={value}>
      {children}
    </SidebarEditContext.Provider>
  );
};

export const useSidebarEditContext = () => useContext(SidebarEditContext);
